// The nav package's one host, mounted by the workspace: the ⌘K / Ctrl+K quick switcher, ⌘1–9 (and
// ⌘⌥ / Ctrl+Alt + 1–9, which browsers never keep for themselves) team switching, and every dialog the
// sidebar's team and category menus ask for through navBus.
import { useCallback, useEffect, useRef, useState } from 'react';
import { data } from '../../../bridge/client';
import { TeamDirectory } from '../../../core/teamDirectory';
import type { SidebarModel } from '../../models/sidebarModel';
import { useShell } from '../../shell/ShellContext';
import { CategoryDeleteDialog, CategoryMenu, CategoryNameDialog } from './CategoryMenus';
import { useNavRequests, type NavRequest } from './navBus';
import { QuickSwitcher } from './QuickSwitcher';
import { isMacPlatform, JoinableTeamsDialog, LeaveTeamDialog, TeamAddDialog, TeamMembersDialog, TeamSheet } from './TeamDialogs';
import { t } from '../../../i18n/i18n';

export function NavHost({
  model,
  teamId,
  currentChannelId,
  onSelectTeam,
  onOpenChannel,
}: {
  model: SidebarModel | null;
  teamId: string | null;
  currentChannelId: string | null;
  onSelectTeam(teamId: string): void;
  onOpenChannel(channelId: string): void;
}) {
  const shell = useShell();
  const [open, setOpen] = useState<NavRequest | null>(null);
  const [menu, setMenu] = useState<Extract<NavRequest, { kind: 'categoryMenu' }> | null>(null);
  const latest = useRef({ model, teamId, onSelectTeam });
  latest.current = { model, teamId, onSelectTeam };

  useNavRequests(
    useCallback((r: NavRequest) => {
      if (r.kind === 'categoryMenu') setMenu(r);
      else setOpen(r);
    }, []),
  );

  useEffect(() => {
    const mac = isMacPlatform();
    const onKey = (e: KeyboardEvent) => {
      if (e.isComposing) return;
      // ⌘K / Ctrl+K: «Cambiar de conversación» (the Mac's menu shortcut; the sidebar field keeps
      // filtering in place for whoever types there).
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (latest.current.model) setOpen({ kind: 'switcher' });
        return;
      }
      const index = TeamDirectory.teamShortcutIndex(e, mac);
      if (index === null) return;
      const teams = latest.current.model?.teams ?? [];
      // An index past the strip is ignored (Mac `macSelectTeamIndex`) — and then the key is the browser's.
      if (index >= teams.length) return;
      e.preventDefault();
      if (teams[index].team.id !== latest.current.teamId) latest.current.onSelectTeam(teams[index].team.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // A team just joined is selected once the store lists it (the worker's reconcile writes it).
  const [pendingTeam, setPendingTeam] = useState<string | null>(null);
  useEffect(() => {
    if (pendingTeam && model?.teams.some((x) => x.team.id === pendingTeam)) {
      setPendingTeam(null);
      onSelectTeam(pendingTeam);
    }
  }, [pendingTeam, model?.teams, onSelectTeam]);

  const close = useCallback(() => setOpen(null), []);
  const closeMenu = useCallback(() => setMenu(null), []);
  const fail = () => shell.toast(t('web.categories.failed'), 'error');

  let dialog = null;
  if (open?.kind === 'switcher' && model) {
    dialog = <QuickSwitcher model={model} currentChannelId={currentChannelId} onOpen={onOpenChannel} onClose={close} />;
  } else if (open?.kind === 'teamSheet' && model && teamId) {
    dialog = <TeamSheet teams={model.teams} currentTeamId={teamId} onSelect={onSelectTeam} onClose={close} />;
  } else if (open?.kind === 'teamMembers') {
    dialog = <TeamMembersDialog teamId={open.teamId} onClose={close} />;
  } else if (open?.kind === 'teamAdd') {
    dialog = <TeamAddDialog teamId={open.teamId} onClose={close} />;
  } else if (open?.kind === 'teamsJoinable') {
    // The joined team arrives with the reconcile the worker ran; the workspace opens it once listed.
    dialog = <JoinableTeamsDialog onJoined={setPendingTeam} onClose={close} />;
  } else if (open?.kind === 'teamLeave') {
    const leaving = open.teamId;
    const name = model?.teams.find((x) => x.team.id === leaving)?.team.displayName ?? '';
    dialog = (
      <LeaveTeamDialog
        teamId={leaving}
        teamName={name}
        onClose={close}
        onLeft={() => {
          const next = TeamDirectory.teamAfterLeaving((latest.current.model?.teams ?? []).map((x) => x.team.id), leaving);
          if (next && leaving === latest.current.teamId) latest.current.onSelectTeam(next);
        }}
      />
    );
  } else if (open?.kind === 'categoryNew') {
    const req = open;
    dialog = <CategoryNameDialog mode="new" initial="" onSubmit={(name) => data.call('categoryCreate', req.teamId, name, req.channelId)} onClose={close} />;
  } else if (open?.kind === 'categoryRename') {
    const req = open;
    dialog = <CategoryNameDialog mode="rename" initial={req.name} onSubmit={(name) => data.call('categoryRename', req.teamId, req.categoryId, name)} onClose={close} />;
  } else if (open?.kind === 'categoryDelete') {
    const req = open;
    dialog = <CategoryDeleteDialog name={req.name} onConfirm={() => void data.call('categoryDelete', req.teamId, req.categoryId).then((ok) => ok || fail(), fail)} onClose={close} />;
  }

  return (
    <>
      {dialog}
      {menu ? <CategoryMenu key={menu.categoryId} teamId={menu.teamId} categoryId={menu.categoryId} at={menu.at} onClose={closeMenu} /> : null}
    </>
  );
}
